// In-game HUD strings (English) — split out of game.ts so each
// locale table lives in its own file; merged in ./game.ts.
// Plurals use `.one` / `.other` key pairs; pick with `plural()` from ./rich.
// The settings/help half lives in ./gameHelpEn.ts.

import type { GameHelpKey } from './gameHelpEn.js';

export const gameEn = {
  // Shared
  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.off': 'Off',
  'common.unknown': 'unknown',
  'common.player': 'Player {player}',
  'city.neutral': 'Neutral village',
  'common.playerShort': 'P{player}',
  'common.playerWins': 'Player {player} wins',
  'common.hp': 'HP',
  'common.hpLower': 'hp',
  // Event log + event feed
  'eventLog.title': 'Event Log',
  'eventLog.label': 'Event log',

  'event.doctrineInvested': 'Doctrine invested',
  'event.unitMoved': 'Unit moved',
  'event.unitCombat': 'Combat',
  'event.unitGarrisoned': 'Unit garrisoned',
  'event.unitDeployed': 'Unit deployed',
  'event.cityAttacked': 'City attacked',
  'event.cityDestroyed': 'City destroyed',
  'event.cityDamaged': 'City damaged',
  'event.buildingStarted': 'Construction started',
  'event.buildingCompleted': 'Building completed',
  'event.heroAbilityActivated': 'Hero ability used',
  'event.heroAbilitySelected': 'Hero ability learned',
  'event.trigger': 'Event',
  'event.merchantDocked': 'A house at your harbour',
  'event.merchantRoute': 'A trade route opens',
  'event.merchantAccepted': 'You take the offer',
  'event.merchantDeclined': 'You refuse the offer',
  'event.detail.doctrineInvested': 'Player {player} → {archetype}',
  'event.detail.unitMoved': 'Unit to ({from}) → ({to})',
  'event.detail.unitCombat': 'vs defender at ({to})',
  'event.detail.unitGarrisoned': 'Garrisoned at city ({coord})',
  'event.detail.unitDeployed': 'Deployed from city ({coord})',
  'event.detail.damage': 'Damage: {damage}',
  'event.detail.cityRazed': 'City razed',
  'event.detail.merchantDocked': 'The {house} offers {gold} gold and {grain} grain a tick.',
  'event.detail.merchantRoute': 'The {house} pays {gold} gold and {grain} grain a tick.',
  'event.detail.merchantAccepted': 'The {house} is trading with you.',
  'event.detail.abilityActivated': 'Slot {slot}: {ability}',
  'event.detail.abilitySelected': 'Learned: {ability}',

  // Victory paths
  'victory.path.conquest': 'Conquest',
  'victory.path.economy': 'Economy',
  'victory.path.science': 'Science',
  'victory.path.faith': 'Faith',
  'victory.path.diplomacy': 'Diplomacy',
  'victory.progress': 'Victory Progress',
  'victory.title.conquest': 'Conquest victory',
  'victory.title.economy': 'Economic victory',
  'victory.title.science': 'Scientific victory',
  'victory.title.faith': 'Faith victory',
  'victory.title.diplomacy': 'Diplomatic victory',
  'victory.title.milestones': 'Milestone victory',
  'victory.desc.conquest': 'Every city on the map is yours.',
  'victory.desc.economy': 'Your treasury and trade network dominate the realm.',
  'victory.desc.science': 'Your accumulated knowledge has unlocked ultimate understanding.',
  'victory.desc.faith': 'Your faith and temples have united the world under your creed.',
  'victory.desc.diplomacy': 'Your honor and alliances have secured lasting peace.',
  'victory.desc.milestones': 'You completed the realm’s great milestones before anyone else.',
  'victory.youWin': 'You win!',
  // Graduation suggestion (rulesets spec §10 / R8).
  'victory.graduation.mastered': 'You’ve mastered {preset}!',
  'victory.graduation.adds': 'It adds {list}.',
  'victory.graduation.tryNext': 'Try {next}',
  'victory.paused':
    'The simulation has paused; start a new match or dismiss this banner to inspect the final board.',
  'victory.dismiss': 'Dismiss victory banner',
  'victory.viewSummary': 'View Summary',
  'victory.viewSummaryLabel': 'View match summary',
  'victory.menu': 'Menu',
  'victory.newMatchLabel': 'Start a new match',
  'victory.matchResult': 'Match result',

  // Post-victory summary
  'summary.title': 'Match Summary',
  'summary.you': '(You)',
  'summary.winner': 'Winner',
  'summary.cities': 'Cities',
  'summary.units': 'Units',
  'summary.doctrines': 'Doctrines',
  'summary.gold': 'Gold',
  'summary.industry': 'Industry',
  'summary.knowledge': 'Knowledge',
  'summary.returnToMenu': 'Return to Menu',
  'summary.playAgain': 'Play Again',

  // Unit selection tray
  'unitTray.label': 'Selected units',
  'unitTray.hero': 'Hero',
  'unitTray.level': 'Lv{level}',
  'unitTray.mp': 'MP',
  'unitTray.emptySlot': 'empty slot',

  // Right panel
  'rightPanel.info': 'INFO',
  'rightPanel.label': 'Match info',
  'rightPanel.matchOverview': 'Match Overview',
  'rightPanel.overview': 'Overview',
  'rightPanel.diplomacy': 'Diplomacy',
  'rightPanel.viewDetails': 'View entity details',
  'rightPanel.closeSelection': 'Close selection',
  'rightPanel.closeInfo': 'Close info panel',
  'selection.tile': 'Tile',
  'selection.city': 'City',
  'selection.unit': 'Unit',
  'selection.units': 'Units ({count})',
  'overview.turnYear': 'Turn {turn} · Year {year}',
  'overview.victoryProgress': 'VICTORY PROGRESS',
  'overview.tracking': 'Tracking…',
  'overview.wonder': 'Wonder',
  'overview.domination': 'Domination',
  'overview.doctrine': 'DOCTRINE',
  'overview.doctrineEmpty': 'Doctrine unlocks available once a player loads.',
  'overview.recentEvents': 'RECENT EVENTS',
  'overview.noEvents': 'No events yet — start exploring.',
  'diplomacy.empty':
    'No other players discovered yet. Encounter another civilization to see relations here.',
  'diplomacy.knownPlayers': 'Known Players ({count})',
  'diplomacy.neutral': 'neutral',

  // Doctrine rail
  'doctrine.label': 'Doctrine selection',
  'doctrine.tab.doctrine': 'Doctrine',
  'doctrine.tab.artifacts': 'Artifacts',
  'doctrine.title': 'DOCTRINES',
  'doctrine.points.one': '{count} point available',
  'doctrine.points.other': '{count} points available',
  'doctrine.nextGrant': ' · next in {ticks}t',
  'doctrine.noPlayer': 'no player loaded',
  'doctrine.unitAttack': '+{bonus} unit attack (Industry)',
  'doctrine.hybridHint': 'invest enough points in two archetypes to unlock {forms}',
  'doctrine.hybridForms': 'hybrid forms',
  'doctrine.next': 'Next: ',
  'doctrine.atCap': 'max tier reached',
  'doctrine.noPoints': 'no points available',
  'doctrine.invest': 'invest',
  'doctrine.investLabel': 'Invest a doctrine point in {name}',
  'doctrine.unspecialized': 'Unspecialized',
  'doctrine.artifactBonus': 'Artifact bonus active',
  'doctrine.artifactBonusLabel': 'artifact bonus',

  'archetype.industry': 'Industry',
  'archetype.industry.blurb': 'Order through making.',
  'archetype.industry.tier1': 'Smithing guilds and craftsmen',
  'archetype.industry.tier2': 'Forges and Mining Guilds',
  'archetype.industry.tier3': 'Mass Industry and Logistics Corps',
  'archetype.industry.tier4': 'Factory Cities and Steampunk Pacts',
  'archetype.industry.tier5': 'Megastructure projects',
  'archetype.science': 'Science',
  'archetype.science.blurb': 'Truth through knowledge.',
  'archetype.science.tier1': 'Scholarship, Wheel and Calendar',
  'archetype.science.tier2': 'Universities and Optics',
  'archetype.science.tier3': 'Printing Press and Astronomy',
  'archetype.science.tier4': 'Computing and Rocketry',
  'archetype.science.tier5': 'Ascended Minds and Singularities',
  'archetype.faith': 'Faith',
  'archetype.faith.blurb': 'Strength through belief.',
  'archetype.faith.tier1': 'Shrines and Tribal Priests',
  'archetype.faith.tier2': 'Temples and Holy Orders',
  'archetype.faith.tier3': 'Missionaries and Theocracy',
  'archetype.faith.tier4': 'Mega-Churches and Miracles',
  'archetype.faith.tier5': 'Apotheosis and Saintly Reliquaries',
  'archetype.arcane': 'Arcane',
  'archetype.arcane.blurb': 'Power through mystery.',
  'archetype.arcane.tier1': 'Hedge Wizards and Ley-Touched',
  'archetype.arcane.tier2': 'Mage Towers and Familiar Bonds',
  'archetype.arcane.tier3': 'Planar Conduits and Binding',
  'archetype.arcane.tier4': 'Spell-Storms and Dragon Pacts',
  'archetype.arcane.tier5': 'Godlike Archmage and Planar Sovereign',
  'archetype.nature': 'Nature',
  'archetype.nature.blurb': 'Harmony through life.',
  'archetype.nature.tier1': 'Druidic Circles and Beast-Tamers',
  'archetype.nature.tier2': 'Sentinels and Ley Line Walkers',
  'archetype.nature.tier3': 'Treant Lords and World Roots',
  'archetype.nature.tier4': 'Leviathan-Bond and World-Shaping',
  'archetype.nature.tier5': 'Gaia Avatar and Biosphere Merge',
  'archetype.chaos': 'Chaos',
  'archetype.chaos.blurb': 'Freedom through disorder.',
  'archetype.chaos.tier1': 'Raider Warbands and Blood Sacrifice',
  'archetype.chaos.tier2': 'Sabotage Cells and Plague-Seeded',
  'archetype.chaos.tier3': 'Warp Rifts and Demon Bound',
  'archetype.chaos.tier4': 'Apocalypse Cults and Reality Warps',
  'archetype.chaos.tier5': 'Chaos Incarnate and False-God Summons',

  'artifacts.title': 'ARTIFACTS',
  'artifacts.collected': '{count} collected',
  'artifacts.empty': 'No artifacts yet. Explore ruins with an Explorer unit.',
  'artifacts.installed': 'Installed',
  'artifacts.install': 'Install in City',
  'artifacts.installedIn': 'Installed in {city}',

  // Biomes (display names for the sim's BiomeKind)
  'biome.plains': 'Plains',
  'biome.forest': 'Forest',
  'biome.hills': 'Hills',
  'biome.mountain': 'Mountain',
  'biome.desert': 'Desert',
  'biome.tundra': 'Tundra',
  'biome.coast': 'Coast',
  'biome.ocean': 'Ocean',
  'biome.unknown': 'Unknown',

  // Tile / city / unit info
  'tile.traversable': 'traversable',
  'tile.impassable': 'impassable',
  'tile.traversableHint': 'Ground units can cross this tile.',
  'tile.impassableHint': 'Blocks ground movement.',
  'tile.biome': 'biome',
  'tile.resource': 'resource',
  'tile.axial': 'axial',
  'tile.variant': 'variant',

  'city.tier1': 'Settlement',
  'city.tier2': 'Town',
  'city.tier3': 'City',
  'city.tier4': 'Metropolis',
  'city.tier5': 'Megacity',
  'city.tierN': 'Tier {tier}',
  'city.tierBadge': '{name} (T{tier})',
  'city.health': 'city health',
  'city.culture': 'culture',
  'culture.imperial': 'Imperial',
  'culture.imperial.blurb': 'White marble, terracotta roofs, columns and domes.',
  'culture.nordic': 'Nordic',
  'culture.nordic.blurb': 'Dark timber halls, steep gables, dragon finials.',
  'culture.eastern': 'Eastern',
  'culture.eastern.blurb': 'Vermilion pillars, swept tiered roofs, pagodas.',
  'city.population': 'population',
  'city.influence': 'influence',
  'city.influenceValue': '{radius} hex',
  'city.garrison': 'garrison',
  'city.garrisonUnits.one': '{count} unit',
  'city.garrisonUnits.other': '{count} units',
  'city.attack': 'Attack city',
  'city.deploy': 'Deploy garrison ({count})',
  'city.doctrine': 'City doctrine',
  'city.doctrine.needsArtifact': 'Off-path — needs an installed artifact',
  'city.doctrine.needsArtifactHint': 'Off-path archetypes need an installed city artifact.',

  'unit.health': 'unit health',
  'unit.movement': 'movement',
  'unit.cargo': 'Cargo',
  'unit.sailing': 'Course',
  'unit.shipHint': 'Click a harbour to load or sell.',
  'ship.shipyard': 'Shipyard',
  'ship.trade': 'Trade ship',
  'ship.transport': 'Transport',
  'ship.warship': 'Warship',
  'ship.galleon': 'Galleon',
  'ship.cost': 'Cost',
  'ship.turns': '{turns} turns',
  'ship.build': 'Build',
  'ship.locked': 'Needs yard',
  'sea.offerTitle': 'A house is at your harbour',
  'sea.offerTerms':
    'The {house} offers {gold} gold and {grain} grain a tick, and {gift} gold to seal the deal.',
  'sea.accept': 'Accept',
  'sea.decline': 'Decline',
  'sea.house1': 'Salt House',
  'sea.house2': 'Amber House',
  'sea.house3': 'Iron House',
  'sea.merchantVessel': 'Merchant vessel',
  'sea.standingTitle': 'Your standing with the houses',
  'sea.standingCold': 'The houses have gone cold on you: they call less often and pay less.',
  'sea.standingShunned': 'The houses are not calling at all for now.',
  'sea.routesTitle': 'Foreign houses',
  'sea.route': '{gold} gold, {grain} grain a tick',
  'sea.routesBlocked': 'Blockaded — these houses pay nothing until the corsairs leave.',
  'unit.water': 'Water',
  'unit.waterLevel': 'unit water supply',
  'unit.formation': 'Formation',
  'unit.regular': 'Regular unit — no hero abilities.',
  'formation.standard': 'Standard',
  'formation.line': 'Line',
  'formation.column': 'Column',
  'formation.wedge': 'Wedge',
  'formation.hold': 'Hold',
  'formation.phalanx': 'Phalanx',
  'formation.skirmish': 'Skirmish',

  'building.title': 'Buildings',
  'building.inProgress': 'Building {name}... ({pct}%)',
  'building.progress': 'building progress',
  'building.queue': 'Queue ({count})',
  'building.recruiting': 'Recruiting {id}',
  'building.building': 'Building {id}',
  'building.itemProgress': '{id} progress',
  'building.requiresTier': 'Requires city tier {tier}',
  'building.requires': 'Requires {building}',
  'building.alreadyQueued': 'Already building or queued here',
  'building.alreadyBuilt': 'Already built',
  'building.tooExpensive': 'Too expensive',
  'building.build': 'Build {name}',
  'building.cost': '{cost} ind.',

  // suggest auto-manage mode (play-modes spec §8)
  'suggest.title': 'City helper suggests',
  'suggest.train': 'Train {name}',

  'preview.title': 'ATTACK PREVIEW',
  'preview.vsNearby': '{kind} vs nearby',
  'preview.favorable': 'Favorable',
  'preview.even': 'Even',
  'preview.unfavorable': 'Unfavorable',
  'preview.terrain': '+{bonus} terrain',
  'preview.terrainTitle': 'Defender terrain bonus: +{bonus}',
  'preview.classCounter': 'Class counter',
  'preview.formationCounter': 'Formation counter',
  'preview.total': 'Total multiplier',

  // Heroes
  'hero.unknown': 'Unknown Hero',
  'hero.level': 'Level',
  'hero.xp': 'XP',
  'hero.wounded': 'Wounded',
  'hero.captured': 'Captured',
  'hero.attributes': 'ATTRIBUTES',
  'hero.attr.martial': 'MAR',
  'hero.attr.intellect': 'INT',
  'hero.attr.charisma': 'CHA',
  'hero.attr.cunning': 'CUN',
  'hero.attr.faith': 'FAI',
  'hero.loyalty': 'Loyalty',
  'hero.traits': 'TRAITS',
  'hero.aura': 'ACTIVE AURA',
  'hero.auraStats': 'Radius {radius} · ATK x{attack} · DEF x{defense}',
  'hero.abilities': 'ABILITIES',
  'hero.emptySlot': 'Slot {slot} — empty',
  'hero.cooldown': 'CD: {ticks}t',
  'hero.use': 'Use',
  'hero.picks.one': '{count} Ability Pick Available',
  'hero.picks.other': '{count} Ability Picks Available',
  'hero.noAbilities': 'No abilities available at this level.',
  'hero.known': '(known)',

  // Entity detail modal
  'entity.entity': 'Entity',
  'entity.population': 'Population: {count}',
  'entity.tier': 'Tier: {tier}',
  'entity.biome': 'Biome: {biome}',
  'entity.resource': 'Resource: {resource}',
  'entity.attack': 'Attack',
  'entity.deploy': 'Deploy',
  'entity.heroLevel': '{archetype} · Level {level}',
  'entity.role': 'Role: {role}',
  'entity.xp': 'XP: {xp}',
  'entity.loyalty': 'Loyalty: {loyalty}',
  'entity.viewDetails': 'View details',

  // Combat overlay + alerts
  'combat.title': 'Combat',
  'combat.titleCount': 'Combat — {count} active',
  'combat.active': '{count} active',
  'combat.tickerLabel.one': 'Combat ticker — {count} active fight',
  'combat.tickerLabel.other': 'Combat ticker — {count} active fights',
  'combat.vs': '{attacker} vs {defender}',
  'combat.moreNearby': '+{count} more nearby',
  'combat.distant.one': 'Continental Pact — {count} settlement lost',
  'combat.distant.other': 'Continental Pact — {count} settlements lost',
  'combat.region.capital': 'capital',
  'combat.region.north': 'north',
  'combat.region.south': 'south',
  'combat.region.east': 'east',
  'combat.region.west': 'west',
  'combat.engagement': 'Combat engagement',
  'combat.minimize': 'Minimize',
  'combat.noRounds': 'No rounds yet. Awaiting first strike.',
  'combat.strike': '{attacker} struck {defender} for {damage} dmg',
  'combat.morale': '{unit} morale {level}',
  'combat.morale.high': 'high',
  'combat.morale.medium': 'medium',
  'combat.morale.low': 'low',
  'combat.morale.critical': 'critical',
  'combat.kill': ' · KILL',
  'toast.label': 'Combat alerts',
  'toast.attacked': 'Your city was attacked!',
  'toast.damage': '−{damage} HP',
  'toast.remaining': '({hp} left)',
  'toast.dismiss': 'Dismiss attack alert',
  'toast.expand': 'Show attack details',
  'toast.collapse': 'Hide attack details',
} as const;

export type GameCoreKey = keyof typeof gameEn;
/** Every in-game HUD key: the core table plus the help/settings half. */
export type GameMessageKey = GameCoreKey | GameHelpKey;
